import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";
import { API_URL } from "../../../config/api";
import bgImage from "../../../Images/login.svg";
import Logo from "../../../Images/Logo.svg";
import RightArrow from "../../../Images/Right Arrow.svg";

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Handle Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // ============================
  // 🔹 Handle Login Submit
  // ============================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/login`,
        {
          email: formData.email,
          password: formData.password,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        },
      );

      const api = response.data;

      if (!api.success) {
        setError(api.error || "Login failed");
        return;
      }

      const tokenData = api.data;

      // Store access token (refresh token stored as a cookie)
      localStorage.setItem("access_token", tokenData.access_token);
      localStorage.setItem("token_type", tokenData.token_type);

      // Get user info
      const userRes = await axios.get(`${API_URL}/current_user`, {
        headers: { Authorization: `Bearer ${tokenData.access_token}` },
      });

      const roles = userRes.data.roles || [];

      if (roles.some((r) => r.name === "student")) {
        navigate("/student_dashboard");
      } else if (roles.some((r) => r.name === "instructor")) {
        navigate("/ins_profile");
      } else if (roles.some((r) => r.name === "super_admin")) {
        navigate("/admin_profile");
      } else if (roles.some((r) => r.name === "org_admin")) {
        navigate("/admin_profile");
      } else if (roles.some((r) => r.name === "training_partner_admin")) {
        navigate("/admin_profile");
      } else {
        setError("Your account does not have access!");
      }
    } catch (err) {
      if (err.response?.data?.detail) {
        const details = err.response.data.detail;

        // If it's a FastAPI validation array
        if (Array.isArray(details)) {
          const msg = details.map((e) => e.msg).join(", ");
          setError(msg);
        } else {
          // If backend sent a normal string
          setError(details.toString());
        }
      } else {
        setError("Login failed. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div
      className="relative flex min-h-screen items-center justify-end bg-gray-50"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="absolute inset-0 bg-black/40" />

      <div className="relative z-10 bg-white rounded-2xl shadow-[0_12px_24px_rgba(32,33,35,0.1)] border border-zinc-200 w-[90%] max-w-md p-8 flex flex-col gap-6 mr-20">
        <div className="flex justify-start mb-2">
          <img src={Logo} alt="SkillsMD Logo" className="w-36 h-auto" />
        </div>

        {/* Form */}
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          {/* Email */}
          <div className="flex flex-col items-start gap-2">
            <label className="text-sm font-bold text-neutral-800">
              Email <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className="h-10 w-full px-3 bg-white rounded-md shadow-[0_0_5px_rgba(32,33,35,0.07)] outline outline-1 outline-zinc-200 text-sm text-zinc-700 focus:outline-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
              required
            />
          </div>

          {/* Password */}
          <div className="flex flex-col items-start gap-2 relative">
            <label className="text-sm font-bold text-neutral-800">
              Password <span className="text-red-600">*</span>
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              className="h-10 w-full px-3 pr-10 bg-white rounded-md shadow-[0_0_5px_rgba(32,33,35,0.07)] outline outline-1 outline-zinc-200 text-sm text-zinc-700 focus:outline-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Error message */}
          {error && (
            <p className="text-red-600 text-sm text-center font-medium">
              {error}
            </p>
          )}

          {/* Links */}
          <div className="flex justify-between items-center text-xs">
            <a
              href="/passwordlink"
              className="text-[#4AACC5] hover:text-[#3E95AC]"
            >
              Forgot Password?
            </a>
            <a
              href="/having_trouble"
              className="text-[#4AACC5] hover:text-[#3E95AC]"
            >
              Having Trouble?
            </a>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            disabled={loading}
            className={`mt-2 w-full px-5 py-2.5 rounded-md text-white text-sm font-bold shadow-sm transition-all ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#4AACC5] hover:bg-[#3E95AC]"
            }`}
          >
            {loading ? "Logging in..." : "Log in"}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-2">
            <div className="flex-1 border-t border-zinc-200" />
            <span className="text-zinc-400 text-xs font-medium">
              Or continue with
            </span>
            <div className="flex-1 border-t border-zinc-200" />
          </div>
          {/* Join Options */}
          {/* Join Options */}
          <div className="flex justify-center items-center gap-6 mt-2">
            <button
              type="button"
              onClick={() => navigate("/student_signup")}
              className="flex items-center gap-2 px-6 py-2.5 border border-[#4AACC5] rounded-lg text-[#4AACC5] text-sm font-semibold hover:bg-[#E8F6F9] transition-all"
            >
              Join as Student
              <span className="text-lg">
                <img src={RightArrow} alt="Right Arrow" className="w-4 h-4" />
              </span>
            </button>

            <button
              type="button"
              onClick={() => navigate("/register_independenttrainer")}
              className="flex items-center gap-2 px-6 py-2.5 border border-[#4AACC5] rounded-lg text-[#4AACC5] text-sm font-semibold hover:bg-[#E8F6F9] transition-all"
            >
              Join as Instructor
              <span className="text-lg">
                <img src={RightArrow} alt="Right Arrow" className="w-4 h-4" />
              </span>
            </button>
          </div>
        </form>

        <div className="self-stretch  text-left">
          <p className="text-zinc-800 text-xs font-normal leading-5">
            By continuing, you agree to{" "}
            <span className="text-[#01B5C5] font-medium cursor-pointer">
              SkillsMD
            </span>{" "}
            Agreement or other agreement for, and the Privacy Notice. This site
            uses essential cookies. See our Cookie Notice for more information.
          </p>
        </div>
      </div>
    </div>
  );
}
